import { useCallback, useMemo } from "react"
import moment from "moment-timezone";
import { useTranslation } from "react-i18next";
import {
    CheckCheck,
    CheckSquare,
    CornerUpRight,
    FileStack,
    LockKeyhole,
    LockKeyholeOpen,
    MinusCircle,
    RefreshCcwDot,
    Save,
    Square,
    Trash2
} from "lucide-react";
import { useMenuContext } from "../../context/useMenuContext";
import { useScheduleContext } from "../../context/useScheduleContext";
import { usePermissions } from "../../usePermissions";
import { useIsTemplate } from "../../useIsTemplate";
import { useFilters } from "../../../../../../utils/hooks/useFilters";
import type { Event } from "../../../types/Event";

export const useDayCellMenu = ({
    openActionsModal,
    openCopyModal,
    openDeleteModal,
    openSaveDayAsTemplate,
    openLoadTemplatesModal
}: any) => {
    const { t } = useTranslation();
    const { selected, closeMenu } = useMenuContext();
    const [allFilters] = useFilters();
    const {
        events,
        calendarEvents,
        unassignedEvents,
        selectedEvents,
        selectedUnassignedEvents,
        type,
        ctrlShiftS,
        dispatch,
    } = useScheduleContext();

    const isTemplate = useIsTemplate()
    const { canCreate, canCopy, canDelete, canLock, canUnlock, canApprove, canDeleteUnassignedShift } = usePermissions();

    const isSameDay = useCallback(
        (date: string) => moment(date).isSame(selected, "day"),
        [selected]
    );

    const dailyEvents = useMemo(() => {
        return (allFilters?.mode !== "month" ? events : (calendarEvents?.[selected]?.events || []))
            ?.filter((event: any) =>
                isSameDay(event.startDate) && event?.type !== "unavailable"
            );
    }, [allFilters?.mode, events, calendarEvents, selected, isSameDay]);

    const dailyUnassignedEvents = useMemo(
        () => unassignedEvents.filter((event: any) => isSameDay(event.startDate)),
        [unassignedEvents, isSameDay]
    );

    const { dailySelectedUnassignedEvents } = useMemo(() => {
        const dailySelectedEvents = selectedEvents.filter(
            (event: any) => isSameDay(event.startDate) && event?.type !== "unavailable"
        );
        const dailySelectedUnassignedEvents = selectedUnassignedEvents?.filter(
            (event: any) => isSameDay(event.startDate)
        );

        return {
            dailySelectedEvents,
            dailySelectedUnassignedEvents,
        };
    }, [selectedEvents, selectedUnassignedEvents, isSameDay]);

    const isChecked = useMemo(() => {
        const allAssignedSelected = dailyEvents.length > 0 && dailyEvents?.length === selectedEvents?.length;
        const allUnassignedSelected = dailyUnassignedEvents.length > 0 &&
            dailyUnassignedEvents?.length === dailySelectedUnassignedEvents?.length;

        return allFilters?.mode === "month" ? (allAssignedSelected || allUnassignedSelected) : allAssignedSelected;
    }, [dailyEvents.length, selectedEvents?.length, dailyUnassignedEvents.length, dailySelectedUnassignedEvents?.length, allFilters?.mode]);

    const onSelectChange = useCallback(() => {
        const hasUnassignedSelected = selectedUnassignedEvents?.length > 0 && allFilters?.mode === "month";
        const payload = (hasUnassignedSelected ||
            (dailyEvents?.length === 0 && selectedEvents?.length === 0 && allFilters?.mode === "month")
        ) ? dailyUnassignedEvents : dailyEvents;
        const ids = payload.map((item: any) => item.id);
        const type = hasUnassignedSelected ? "select-unassigned-events" : "select-events";
        if (isChecked) {
            const values = hasUnassignedSelected
                ? selectedUnassignedEvents.filter((event: Partial<Event>) => !ids.includes(event.id))
                : selectedEvents.filter((event: Event) => !ids.includes(event.id));
            dispatch({ type, payload: values });
        } else {
            dispatch({
                type,
                payload: [
                    ...(hasUnassignedSelected ? selectedUnassignedEvents : selectedEvents),
                    ...payload
                ]
            });
        }

        if (!ctrlShiftS) {
            dispatch({ type: "ctrl-shift-s" })
        }
    }, [selectedUnassignedEvents, allFilters?.mode, dailyEvents, selectedEvents, dailyUnassignedEvents, isChecked, ctrlShiftS, dispatch]);

    const hasDeletableEvents = useMemo(
        () => dailyEvents?.length > 0 && !dailyEvents?.every((event: any) => event?.locked),
        [dailyEvents]
    );

    // in month mode the day cell also owns the unassigned shifts of that day
    const hasDeletableUnassigned = useMemo(
        () => allFilters?.mode === "month" &&
            canDeleteUnassignedShift &&
            dailyUnassignedEvents?.length > 0,
        [allFilters?.mode, canDeleteUnassignedShift, dailyUnassignedEvents]
    );

    return useMemo(() => {
        const menuItems = [
            {
                icon: isChecked ? CheckSquare : Square,
                label: t("select"),
                onChange: onSelectChange,
                hidden: (
                    !canCopy &&
                    !canLock &&
                    !canUnlock &&
                    !canApprove &&
                    !canDelete
                ),
                disabled: allFilters?.mode === "month"
                    ? (dailyEvents?.length === 0 && dailyUnassignedEvents?.length === 0)
                    : dailyEvents?.length === 0
            },
            {
                icon: CheckCheck,
                label: t("submit"),
                onChange: () => {
                    openActionsModal({
                        type: "bulk-day",
                        day: selected,
                        ids: selectedEvents?.map((event: any) => event.id),
                        action: "submit"
                    });
                    closeMenu();
                },
                hidden: !canCreate || isTemplate || type !== "home",
                disabled: dailyEvents?.every((event: any) => ["approved", "submitted"].includes(event?.status) || event?.locked),
            },
            {
                icon: CornerUpRight,
                label: t("publish"),
                onChange: () => {
                    openActionsModal({
                        type: "bulk-day", // allFilters?.mode === "month" ? "bulk-day" : "day",
                        day: selected,
                        ids: selectedEvents?.map((event: any) => event.id),
                        action: "publish"
                    });
                    closeMenu();
                },
                hidden: !canApprove || isTemplate,
                disabled: dailyEvents?.every((event: any) => event?.status === "approved" || event?.locked),
            },
            {
                icon: MinusCircle,
                label: t("unpublish"),
                onChange: () => {
                    openActionsModal({
                        type: "bulk-day", // allFilters?.mode === "month" ? "bulk-day" : "day",
                        day: selected,
                        ids: selectedEvents?.map((event: any) => event.id),
                        action: "unpublish"
                    });
                    closeMenu();
                },
                hidden: !canApprove || isTemplate,
                disabled: dailyEvents?.every((event: any) => event?.status === "pending" || event?.locked),
            },
            {
                icon: LockKeyhole,
                label: t("lock"),
                onChange: () => {
                    openActionsModal({
                        type: "bulk-day", // allFilters?.mode === "month" ? "bulk-day" : "day",
                        day: selected,
                        ids: selectedEvents?.map((event: any) => event.id),
                        action: "lock"
                    });
                    closeMenu();
                },
                hidden: !canLock || isTemplate,
                disabled: dailyEvents?.every((event: any) => event?.locked)
            },
            {
                icon: LockKeyholeOpen,
                label: t("unlock"),
                onChange: () => {
                    openActionsModal({
                        type: "bulk-day", // allFilters?.mode === "month" ? "bulk-day" : "day",
                        day: selected,
                        ids: selectedEvents?.map((event: any) => event.id),
                        action: "unlock"
                    });
                    closeMenu();
                },
                hidden: !canUnlock || isTemplate,
                disabled: dailyEvents?.every((event: any) => !event?.locked)
            },
            {
                icon: FileStack,
                label: t("copy"),
                onChange: () => {
                    const ids = selectedEvents?.map((event: any) => event?.id);
                    openCopyModal({
                        ids,
                        date: selected,
                        type: "bulk-day", //allFilters?.mode === "month" ? "bulk-day" : "day",
                        isTemplate
                    });
                    closeMenu();
                },
                hidden: !canCopy,
                disabled: dailyEvents?.length === 0 || selectedEvents?.length > 0 || dailyEvents?.every((event: any) => event?.locked),
            },
            {
                icon: Trash2,
                label: t("delete"),
                onChange: () => {
                    // in month mode the day owns both, so bulk-day and bulk-unassign run for that day
                    const unassignedIds = hasDeletableUnassigned
                        ? dailyUnassignedEvents?.map((event: any) => event.id)
                        : [];
                    openDeleteModal({
                        ids: [],
                        unassignedIds,
                        hasEvents: hasDeletableEvents,
                        type: "bulk-day",
                        day: selected,
                        isTemplate
                    });
                    closeMenu();
                },
                hidden: !canDelete,
                disabled: !hasDeletableEvents && !hasDeletableUnassigned,
            },
            // {
            //     icon: Save,
            //     label: t("save-as-template"),
            //     onChange: () => {
            //         openSaveDayAsTemplate(selected);
            //         closeMenu();
            //     },
            //     hidden: !canCreate || isTemplate || ["my-profile", "home"].includes(type),
            //     disabled: dailyEvents?.length === 0
            // },
            {
                icon: RefreshCcwDot,
                label: t("load-day-template"),
                onChange: () => openLoadTemplatesModal({ date: selected, type: "day" }),
                hidden: !canApprove || isTemplate || ["my-profile", "home"].includes(type),
                disabled: false,
            }
        ];
        return menuItems.filter((item: any) => !item.hidden);
    }, [
        type,
        selectedEvents,
        allFilters,
        dailyEvents,
        dailyUnassignedEvents,
        hasDeletableEvents,
        hasDeletableUnassigned,
        canCopy,
        canLock,
        canUnlock,
        canApprove,
        canDelete,
        canCreate,
        isChecked,
        isTemplate,
        selected,
        openActionsModal,
        openCopyModal,
        openDeleteModal,
        // openSaveDayAsTemplate,
        openLoadTemplatesModal,
        onSelectChange,
        closeMenu,
        t
    ]);
}
